// flapSlabs.js — the Flap slabs tab (V2 #54, criterion No. 30).
//
// A superuser sets the platform's flap-slab table here; every flap Mono makes
// then follows it. This module is the tab's logic, kept pure so
// `node src/flapSlabs.test.js` can execute it; Engine.jsx only renders.
//
// NO SLAB NUMBERS LIVE IN THIS REPO. The table comes from
// GET /admin/flap-slabs, the built-in slabs shown while none is set come from
// the same response (the engine describes its own), and every rule a table is
// held to is the ENGINE's validator, answered by the backend in the save
// error's message. This file only converts between the stored table and the
// form a person types into, and words the outcomes.
//
// THE TABLE'S SHAPE (all millimetres; the backend's docstring is the source):
//   rows keyed on the SIDE panel, whole mm, both ends inclusive;
//   glue_mm / lip_mm        absolute [min, max]
//   dust_offset_mm /        [min, max] ADDED to half the side panel, so
//   bottom_offset_mm        "W/2 − (0 to 10)" is [-10, 0], "W/2 + (8 to 10)" [8, 10]
// Every flap defaults to the maximum of its range.

export const FAMILIES = [
  { key: 'bottom_lock', title: 'Bottom-lock cartons', sub: 'BL and inner-lock (IL)',
    flaps: ['glue_mm', 'lip_mm', 'dust_offset_mm', 'bottom_offset_mm'] },
  { key: 'tuck_end', title: 'Tuck-end cartons', sub: 'RTE and STE',
    flaps: ['glue_mm', 'lip_mm', 'dust_offset_mm'] },
];

export const FLAP_LABELS = {
  glue_mm: 'Glue flap',
  lip_mm: 'Tuck-in lip',
  dust_offset_mm: 'Dust flaps',
  bottom_offset_mm: 'Bottom lock',
};

/** Flaps sized from half the side panel (W/2 ± a range) rather than absolutely. */
export const HALF_W = ['dust_offset_mm', 'bottom_offset_mm'];

const MINUS = '−';   // the sign a person reads; '-' is accepted on input too

function str(v) { return v === null || v === undefined ? '' : String(v); }
function noNegZero(v) { return Object.is(v, -0) ? 0 : v; }

/** One stored cell as the sheet writes it: a sign (W/2 flaps only) and two
 *  non-negative sizes. [-10, 0] is − 0 to 10; [6, 9] is + 6 to 9. */
function cellParts(key, pair) {
  const [lo, hi] = pair;
  if (!HALF_W.includes(key)) return { sign: null, a: lo, b: hi };
  if (hi <= 0) return { sign: MINUS, a: noNegZero(-hi), b: noNegZero(-lo) };
  return { sign: '+', a: lo, b: hi };
}

/** One stored cell -> the form's two boxes, written the way the sheet writes
 *  it: a FIXED size ("12mm") fills one box and leaves the other empty, and
 *  EXACTLY half the side panel ("W/2") leaves both empty. */
function cellToForm(key, pair) {
  const half = HALF_W.includes(key);
  if (pair[0] === null || pair[1] === null) {
    return half ? { sign: MINUS, a: '', b: '' } : { a: '', b: '' };
  }
  const { sign, a, b } = cellParts(key, pair);
  if (half && a === 0 && b === 0) return { sign: MINUS, a: '', b: '' };
  const boxes = { a: str(a), b: a === b ? '' : str(b) };
  return half ? { sign, ...boxes } : boxes;
}

/** A cell's two boxes -> the stored [lo, hi]. The ONE reading of the boxes,
 *  shared by what is saved, the form's live reading and the table:
 *    two numbers          a range ("8 to 10 mm")
 *    one number, one empty  a fixed size ("12 mm"; "W/2 − 25 mm")
 *    both empty           W/2 flaps: exactly half the side panel ("W/2");
 *                         glue / lip: no size at all — refused, never 0.  */
function cellToRange(key, cell, at) {
  const name = FLAP_LABELS[key];
  const half = HALF_W.includes(key);
  const ta = String(cell?.a ?? '').trim();
  const tb = String(cell?.b ?? '').trim();
  if (ta === '' && tb === '') {
    if (half) return [0, 0];
    throw new FormError(`${at}: ${name} needs a size — two numbers for a range, `
      + 'or one for a fixed size.');
  }
  const a = ta === '' ? null : num(ta, `${at}: ${name}, smallest`);
  const b = tb === '' ? null : num(tb, `${at}: ${name}, largest`);
  const lo = a ?? b;
  const hi = b ?? a;
  if (!half) return [lo, hi];
  const minus = cell.sign === MINUS || cell.sign === '-';
  return minus ? [noNegZero(-hi), noNegZero(-lo)] : [lo, hi];
}

// ── the draft: rows with identities ──────────────────────────────────────
// The screen edits a DRAFT of the table: the table lists the rows, "Add row" or
// a row's gear opens a form for ONE row, and "Save row" puts it back into the
// draft. Nothing reaches jobs until "Save table" (the backend validates the
// table WHOLE — rows contiguous, both families present — so a row on its own
// could not be saved anyway). Every draft row carries an `id`, never sent to
// the backend, so the screen can say which rows are new or edited, and the form
// edits a row by identity rather than by a position that re-sorting changes.

let lastId = 0;
function newId() { lastId += 1; return `row${lastId}`; }

export function blankRow(familyKey) {
  const fam = FAMILIES.find((f) => f.key === familyKey);
  const row = { id: newId(), from: '', to: '' };
  fam.flaps.forEach((k) => {
    row[k] = HALF_W.includes(k) ? { sign: MINUS, a: '', b: '' } : { a: '', b: '' };
  });
  return row;
}

/** Stored table (or null = none set) -> the editor's draft. No table means
 *  empty families: the table says "no rows yet" and "Add row" starts one. */
export function toForm(table) {
  const form = {};
  FAMILIES.forEach((fam) => {
    const rows = Array.isArray(table?.[fam.key]) ? table[fam.key] : [];
    form[fam.key] = rows.map((r) => {
      const row = { id: newId(), from: str(r.side_from_mm), to: str(r.side_to_mm) };
      fam.flaps.forEach((k) => { row[k] = cellToForm(k, r[k] || [null, null]); });
      return row;
    });
  });
  return form;
}

/** A deep copy (the loaded table and the draft must not share rows). */
export function cloneForm(form) {
  return form ? JSON.parse(JSON.stringify(form)) : form;
}

export class FormError extends Error {}

function num(text, where) {
  const t = String(text ?? '').trim();
  if (t === '') throw new FormError(`${where} is empty.`);
  const n = Number(t);
  if (!Number.isFinite(n)) throw new FormError(`${where} is not a number ("${t}").`);
  return n;
}

/** One draft row -> the stored row. Only turns text into numbers. */
function rowFromForm(fam, row, at) {
  const r = {
    side_from_mm: num(row.from, `${at}: side panel "from"`),
    side_to_mm: String(row.to ?? '').trim() === '' ? null : num(row.to, `${at}: side panel "to"`),
  };
  fam.flaps.forEach((k) => { r[k] = cellToRange(k, row[k] || {}, at); });
  return r;
}

/** The draft -> the table PUT /admin/flap-slabs takes. Only turns text into
 *  numbers; whether the table is VALID is the engine's call, answered by the
 *  backend with a message naming the row and the rule. */
export function fromForm(form) {
  const out = { units: 'mm' };
  FAMILIES.forEach((fam) => {
    out[fam.key] = (form[fam.key] || []).map(
      (row, i) => rowFromForm(fam, row, `${fam.title}, row ${i + 1}`));
  });
  return out;
}

/** Can this one row go into the draft? null, or what is missing, in words.
 *  Parsing only — the RULES are the engine's, applied at "Save table". */
export function rowProblem(familyKey, row) {
  const fam = FAMILIES.find((f) => f.key === familyKey);
  try {
    rowFromForm(fam, row, 'This row');
    return null;
  } catch (e) {
    if (e instanceof FormError) return e.message;
    throw e;
  }
}

/** Soft warnings while a row is typed. They never block "Save row": the
 *  engine's validator is the authority, at "Save table". */
export function rowHints(familyKey, row) {
  const fam = FAMILIES.find((f) => f.key === familyKey);
  const hints = [];
  const from = tryNum(row?.from);
  const to = tryNum(row?.to);
  if (from !== null && to !== null && to < from) {
    hints.push('The side panel "to" is smaller than "from".');
  }
  fam.flaps.forEach((k) => {
    const a = tryNum(row?.[k]?.a);
    const b = tryNum(row?.[k]?.b);
    if (a !== null && b !== null && a > b) {
      hints.push(`${FLAP_LABELS[k]}: the smallest size is bigger than the largest.`);
    }
  });
  return hints;
}

/** One field of one row, changed (pure: a new row). */
export function setRowCell(row, key, patch) {
  if (key === 'from' || key === 'to') return { ...row, [key]: patch };
  return { ...row, [key]: { ...row[key], ...patch } };
}

function byFrom(a, b) {
  const x = tryNum(a.from) ?? Infinity;
  const y = tryNum(b.from) ?? Infinity;
  return x === y ? 0 : (x < y ? -1 : 1);
}

/** "Save row": replace the row with the same id, or add it — then keep the
 *  family in side-panel order, so a row added for 51 to 70 lands between the
 *  rows around it. (Array sort is stable: equal "from"s keep their order.) */
export function upsertRow(form, familyKey, row) {
  const rows = form[familyKey] || [];
  const next = rows.some((r) => r.id === row.id)
    ? rows.map((r) => (r.id === row.id ? row : r))
    : [...rows, row];
  return { ...form, [familyKey]: [...next].sort(byFrom) };
}

export function removeRowById(form, familyKey, id) {
  return { ...form, [familyKey]: (form[familyKey] || []).filter((r) => r.id !== id) };
}

/** A fresh row for "Add row". It starts where the table ends — the last row's
 *  "to" + 1, when that is a number — and copies the last row's minus/plus
 *  choices: the user's own table, never a number of ours. */
export function newRowFor(form, familyKey) {
  const row = blankRow(familyKey);
  const rows = form?.[familyKey] || [];
  const last = rows[rows.length - 1];
  if (last) {
    const to = tryNum(last.to);
    if (to !== null) row.from = String(to + 1);
    HALF_W.forEach((k) => {
      if (row[k] && last[k]?.sign) row[k] = { ...row[k], sign: last[k].sign };
    });
  }
  return row;
}

function rowKey(fam, row) {
  try {
    return JSON.stringify(rowFromForm(fam, row, ''));
  } catch {
    return JSON.stringify({ ...row, id: undefined });   // JSON drops undefined
  }
}

/** 'new' | 'edited' | null — this draft row against the table as loaded. */
export function rowStatus(loaded, draft, familyKey, id) {
  const fam = FAMILIES.find((f) => f.key === familyKey);
  const now = (draft?.[familyKey] || []).find((r) => r.id === id);
  if (!now) return null;
  const before = (loaded?.[familyKey] || []).find((r) => r.id === id);
  if (!before) return 'new';
  return rowKey(fam, before) === rowKey(fam, now) ? null : 'edited';
}

/** What the draft changes, per family, in words — and how many rows. */
export function changesSummary(loaded, draft) {
  const parts = [];
  let total = 0;
  FAMILIES.forEach((fam) => {
    const was = loaded?.[fam.key] || [];
    const now = draft?.[fam.key] || [];
    const added = now.filter((r) => !was.some((w) => w.id === r.id)).length;
    const removed = was.filter((w) => !now.some((r) => r.id === w.id)).length;
    const changed = now.filter((r) => rowStatus(loaded, draft, fam.key, r.id) === 'edited').length;
    const bits = [];
    if (added) bits.push(`${added} row${added > 1 ? 's' : ''} added`);
    if (changed) bits.push(`${changed} changed`);
    if (removed) bits.push(`${removed} removed`);
    if (bits.length) {
      parts.push(`${fam.title}: ${bits.join(', ')}`);
      total += added + changed + removed;
    }
  });
  return { total, text: parts.join(' · ') };
}

/** Read-only wording for one stored cell. */
export function describeCell(key, pair) {
  if (!Array.isArray(pair)) return '—';
  const { sign, a, b } = cellParts(key, pair);
  const span = a === b ? `${a}` : `${a} to ${b}`;
  if (!HALF_W.includes(key)) return `${span} mm`;
  if (a === 0 && b === 0) return 'W/2';
  return `W/2 ${sign} ${a === b ? `${a} mm` : `(${span} mm)`}`;
}

/** The minus/plus choice for a W/2 flap, in words. */
export const SIGN_OPTIONS = [
  { value: MINUS, label: 'minus' },
  { value: '+', label: 'plus' },
];

function tryNum(text) {
  const t = String(text ?? '').trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** One form cell in the sheet's own words, AS TYPED. It reads the boxes with
 *  the same parser the save uses (cellToRange), so the form, the table and the
 *  saved table can never disagree — and it never throws: a size not given yet,
 *  or not a number, shows as '…'. */
export function previewCell(key, cell) {
  try {
    return describeCell(key, cellToRange(key, cell || {}, ''));
  } catch {
    return '…';
  }
}

/** A form row's side-panel range in words. An empty "to" reads as "plus" —
 *  only the last row may be open, and the backend says so if another is. */
export function previewSide(row) {
  const from = tryNum(row?.from);
  if (from === null) return '…';
  if (String(row?.to ?? '').trim() === '') return describeRow({ side_from_mm: from, side_to_mm: null });
  const to = tryNum(row.to);
  return to === null ? '…' : describeRow({ side_from_mm: from, side_to_mm: to });
}

/** The live preview of one family: the sheet's rows, from the form. */
export function previewRows(familyKey, rows) {
  const fam = FAMILIES.find((f) => f.key === familyKey);
  return (rows || []).map((row) => ({
    id: row.id,
    side: previewSide(row),
    cells: fam.flaps.map((k) => previewCell(k, row[k])),
  }));
}

export function describeRow(row) {
  if (row.side_to_mm === null || row.side_to_mm === undefined) return `${row.side_from_mm} mm plus`;
  if (row.side_to_mm === row.side_from_mm) return `${row.side_from_mm} mm`;
  return `${row.side_from_mm} to ${row.side_to_mm} mm`;
}

/** Who saved, as a person reads it: the name the backend looked up (the
 *  display name, or the email when none is on file). The stored id is only a
 *  last resort, for a user no longer on file. */
export function whoSaved(name, id) {
  return name || id || null;
}

/** The status card's rows. */
export function statusRows(view) {
  if (!view) return [];
  const by = whoSaved(view.updated_by_name, view.updated_by);
  if (view.table === null || view.table === undefined) {
    return [
      ['In force', 'Built-in slabs (no table set)'],
      ['Version', view.version === null ? 'never set' : `${view.version} (cleared)`],
      ['Changed', view.updated_at || null],
      ['By', by],
    ];
  }
  return [
    ['In force', 'Admin table'],
    ['Version', String(view.version)],
    ['Changed', view.updated_at || null],
    ['By', by],
    ['Fingerprint', view.digest ? view.digest.slice(0, 12) : null],
  ];
}

/** The engine's own description of its built-in slabs, as lines to show. */
export function builtinLines(builtin) {
  if (!builtin) return [];
  const lines = [`Rows are chosen by the ${builtin.row_by}.`];
  FAMILIES.forEach((fam) => {
    const b = builtin[fam.key];
    if (!b) return;
    ['glue_mm', 'lip_mm'].forEach((k) => {
      (b[k] || []).forEach((band) => {
        const upto = band.front_below_mm !== undefined ? `under ${band.front_below_mm}`
          : band.front_up_to_mm !== undefined ? `up to ${band.front_up_to_mm}` : 'and above';
        lines.push(`${fam.title} — ${FLAP_LABELS[k]}: front panel ${band.front_from_mm} mm `
          + `${upto}${upto === 'and above' ? '' : ' mm'}: ${band.range[0]} to ${band.range[1]} mm, `
          + `default ${band.default} mm`);
      });
    });
    if (b.dust) lines.push(`${fam.title} — Dust flaps: ${b.dust}`);
    if (b.bottom) lines.push(`${fam.title} — Bottom lock: ${b.bottom}`);
  });
  return lines;
}

/** The three backend calls, as [path, options] — the ONE place their shape is
 *  written. api.js spreads these into `request`, so what this file's tests
 *  check is what the app sends. */
export const FLAP_SLAB_ROUTES = {
  load: () => ['/admin/flap-slabs', {}],
  save: (table, expectedVersion) => ['/admin/flap-slabs', {
    method: 'PUT', body: { table, expected_version: expectedVersion },
  }],
  versions: (limit = 20) => [`/admin/flap-slabs/versions?limit=${limit}`, {}],
};

/** Save, then RELOAD from the backend — the screen shows what was stored, not
 *  what was typed, so a value the backend normalised is never mistaken for
 *  the one in force. `client` has `saveFlapSlabs` and `flapSlabs` (api.js's,
 *  or a stand-in in the tests). `table` null = back to the built-in slabs. */
export async function saveAndReload(client, table, loadedVersion) {
  const saved = await client.saveFlapSlabs(table, loadedVersion);
  const view = await client.flapSlabs();
  return { view, form: toForm(view.table), message: saveResultText(saved) };
}

export function saveResultText(resp) {
  if (!resp) return '';
  if (!resp.changed) return 'No change — that is already the table in force.';
  if (resp.table === null) {
    return `Saved as version ${resp.version}: Mono is back on its built-in slabs for every new job.`;
  }
  return `Saved as version ${resp.version}: every new job's flaps now follow this table.`;
}

// The backend names rows and fields by their storage keys ("bottom_lock row 3
// (71 to 100 mm) glue_mm: …"). Shown to a person, those become the words this
// screen uses. Order matters only for keys that contain one another; none do.
const KEY_WORDS = [
  ['bottom_lock', 'Bottom-lock cartons'], ['tuck_end', 'Tuck-end cartons'],
  ['side_from_mm', 'side panel "from"'], ['side_to_mm', 'side panel "to"'],
  ['glue_mm', 'Glue flap'], ['lip_mm', 'Tuck-in lip'],
  ['dust_offset_mm', 'Dust flaps'], ['bottom_offset_mm', 'Bottom lock'],
];

export function friendlyMessage(message) {
  let out = String(message ?? '');
  KEY_WORDS.forEach(([key, words]) => { out = out.split(key).join(words); });
  return out;
}

/** Save errors. A refused table's message is the useful part — it names the
 *  row and the rule — so it is shown whole, in this screen's words. */
export function saveErrorText(err, errorText) {
  if (err instanceof FormError) return err.message;
  if (err?.code === 'flap_slabs_invalid') {
    return `Not saved — the table was refused: ${friendlyMessage(err.message)}`;
  }
  return errorText(err);
}

/** How to fill the boxes, shown in the row form. */
export const BOXES_HINT = 'Two numbers make a range. For a fixed size (like '
  + '“12 mm”), fill one box and leave the other empty. For dust flaps and the '
  + 'bottom lock, leave both empty for exactly half the side panel (“W/2”).';

export const USE_BUILTIN_COPY = {
  title: 'Go back to the built-in slabs?',
  body: [
    'Every new job will size its flaps from Mono\'s built-in slabs instead of '
    + 'this table. Jobs already submitted keep the table they started with.',
    'The current table stays in the history and can be entered again.',
  ],
  confirmLabel: 'Use built-in slabs',
};

export const APPLIES_TO_NOTE = 'This table applies to every organisation and '
  + 'to every flap Mono generates: export, planner, and the editor\'s tile and '
  + 'preview. A designer\'s slider or a saved product range can make a row '
  + 'narrower, never wider. Rows are chosen by the SIDE panel (the panel with '
  + 'the composition and batch area), rounded to the nearest whole mm; W is '
  + 'that side panel. Each flap uses the biggest size its row allows.';
